import { CHAT_CONTEXT_TEXT_MAX, CHAT_TITLE_MAX, ChatMailRef, type Chat, type MailAddress, type MailListItem } from '@jerome2/shared';
import { app } from './app.ts';
import { chatStore } from './chat.ts';
import { MAILS_RESET, nextFocus } from './chatTabs.ts';
import { activateTab, openTab, renameTab, tabs, updateTabState, type PaneId, type Tab, type TabsState } from './tabs.ts';

// Een tabblad per gevlagde mail (ticket 30961, opdracht van de eigenaar: "dat ik op zo'n mail kan klikken, en dat er dan een tab
// per mail opent. Dat ik dan kan chatten over die mail."). Zo'n tabblad is een chattabblad met de mail in de tabstaat
// (`state.mail`, de velden van een rij in het mailpaneel): de titel is het onderwerp, het mailpaneel toont de mail naast het
// gesprek, en het gesprek ontstaat pas bij het eerste bericht, met de mail als context (de server zet die in de systeemprompt) en
// het onderwerp als titel. Een klik op een mail in de kolom Gevlagd (`openMailTab`):
// (a) maakt een tabblad van die mail actief, in welk paneel ook (eerst het actieve paneel en daarin het actieve tabblad);
// (b) kent de client een gesprek over die mail (`context.messageId`; een actief gesprek vóór een gearchiveerd, dan het nieuwste)
//     en staat dat in een chattabblad, dan maakt hij dat tabblad actief en hangt hij het aan de mail;
// (c) anders opent hij een nieuw tabblad van de mail in het actieve paneel, met dat gesprek als het er is.
// Op de telefoon (één paneel zonder tabbalk) komt de mail in het chattabblad dat er al is, zoals een gesprek uit de lijst; pas
// zonder chattabblad komt er een nieuw. Een tweede klik op dezelfde mail opent dus nooit een tweede tabblad.

/** De mail uit de tabstaat (`state.mail`), of null; de tabstaat komt uit localStorage, dus gecontroleerd. */
export function mailOf(value: unknown): ChatMailRef | null {
  if (!value) return null;
  const r = ChatMailRef.safeParse(value);
  return r.success ? r.data : null;
}

/** De mail van een chattabblad, of null. */
export function tabMail(tab: Pick<Tab, 'kind' | 'state'> | null | undefined): ChatMailRef | null {
  return tab && tab.kind === 'chat' ? mailOf(tab.state['mail']) : null;
}

/** Een rij uit de lijst Gevlagd als mail van een tabblad (dezelfde velden als een rij in het mailpaneel). */
export function mailRefOf(item: MailListItem): ChatMailRef {
  return { id: item.id, subject: item.subject, from: item.from, received: item.received, unread: !item.isRead, flagged: item.flagged, hasAttachments: item.hasAttachments };
}

/** De afzender zoals de systeemprompt hem noemt: "Naam <adres>", of alleen het adres. */
export function senderText(from: MailAddress | null): string {
  if (!from) return '';
  const name = from.name.trim();
  return name && name !== from.address ? `${name} <${from.address}>` : from.address;
}

/** De titel van het tabblad en van het gesprek: het onderwerp, of een vaste tekst zonder onderwerp. */
export function mailTitle(mail: Pick<ChatMailRef, 'subject'>): string {
  return mail.subject.trim() || '(geen onderwerp)';
}

/** Wat een nieuw gesprek over deze mail meekrijgt (POST /api/chats): het onderwerp als titel en de mail als context, binnen de
 * grenzen van de server (`CHAT_TITLE_MAX` en `CHAT_CONTEXT_TEXT_MAX`). Van de context zet de server alleen het id in de
 * systeemprompt ("Dit gesprek gaat over de mail met id …", `contextText`); onderwerp en afzender komen van buiten. */
export function mailChatBody(mail: ChatMailRef): { title: string; context: Record<string, unknown> } {
  const text = (value: string) => value.slice(0, CHAT_CONTEXT_TEXT_MAX);
  return { title: mailTitle(mail).slice(0, CHAT_TITLE_MAX), context: { messageId: mail.id, subject: text(mail.subject), from: text(senderText(mail.from)) } };
}

/** Het id van de mail waar een gesprek over gaat (`context.messageId`), of null. */
export function chatMailId(chat: Pick<Chat, 'context'>): string | null {
  const id = chat.context?.['messageId'];
  return typeof id === 'string' && id ? id : null;
}

/** Het gesprek over deze mail dat de client kent: een actief gesprek vóór een gearchiveerd, dan het nieuwste. */
export function chatForMail(chats: Record<string, Chat>, messageId: string): Chat | null {
  const time = (c: Chat) => c.lastMessageAt ?? c.createdAt;
  const matching = Object.values(chats).filter((c) => c.kind === 'chat' && chatMailId(c) === messageId);
  return matching.sort((a, b) => Number(a.archived) - Number(b.archived) || time(b).localeCompare(time(a)))[0] ?? null;
}

/** De mails waar al een gesprek over is (het kenmerk in de kolom Gevlagd). */
export function mailsWithChat(chats: Record<string, Chat>): Set<string> {
  const ids = new Set<string>();
  for (const c of Object.values(chats)) {
    const id = c.kind === 'chat' ? chatMailId(c) : null;
    if (id) ids.add(id);
  }
  return ids;
}

/** De mail die de kolom Gevlagd markeert: die van het actieve tabblad van het actieve paneel, als dat een tabblad van een mail is. */
export function markedMail(s: TabsState, pane: PaneId): string | null {
  const id = s.panes[pane].activeTabId;
  return tabMail(id ? s.tabs[id] : null)?.id ?? null;
}

/** Wat een klik op een mail doet: (a) en (b) een tabblad actief maken (`attach`: een chattabblad dat nog niet aan de mail hing),
 * op de telefoon de mail in het chattabblad zetten, of (c) een nieuw tabblad openen; `chatId` is het bekende gesprek. */
export type MailTabChoice = { kind: 'activate'; tabId: string; attach: boolean } | { kind: 'set'; tabId: string; chatId: string | null } | { kind: 'open'; pane: PaneId; chatId: string | null };

/** Een klik op een mail in de kolom Gevlagd (zie boven). `pane` is het actieve paneel dat in beeld is; `narrow` de telefoon. */
export function chooseMailTab(s: TabsState, chats: Record<string, Chat>, messageId: string, pane: PaneId, narrow: boolean): MailTabChoice {
  const other: PaneId = pane === 'left' ? 'right' : 'left';
  const ordered = ([pane, other] as const).flatMap((p) => {
    const { tabIds, activeTabId } = s.panes[p];
    return activeTabId ? [activeTabId, ...tabIds.filter((id) => id !== activeTabId)] : tabIds;
  });
  const own = ordered.find((id) => tabMail(s.tabs[id])?.id === messageId);
  if (own) return { kind: 'activate', tabId: own, attach: false };
  const chat = chatForMail(chats, messageId);
  const withChat = chat ? ordered.find((id) => s.tabs[id]?.kind === 'chat' && s.tabs[id]?.state['chatId'] === chat.id) : undefined;
  if (withChat) return { kind: 'activate', tabId: withChat, attach: true };
  const chatId = chat?.id ?? null;
  if (narrow) {
    const left = s.panes.left;
    const reuse = [left.activeTabId, ...left.tabIds].find((id) => id !== null && s.tabs[id]?.kind === 'chat');
    if (reuse) return { kind: 'set', tabId: reuse, chatId };
  }
  return { kind: 'open', pane: narrow ? 'left' : pane, chatId };
}

/** Een klik op een mail in de kolom Gevlagd (of het vel Gevlagd op de telefoon); geeft het tabblad waar de mail nu in beeld staat.
 * Het mailpaneel staat open op de mail, ook als het tabblad er al was. De mail is gekozen en dus vastgehouden (`mailsHold`, zie
 * ChatScreen): een resultaat in een bekend gesprek, zoals mails die Jerome eerder vond, zet het paneel er niet vanaf; het volgende
 * bericht van de eigenaar laat los. */
export function openMailTab(mail: ChatMailRef, pane: PaneId): string {
  const choice = chooseMailTab(tabs.get(), chatStore.get().chats, mail.id, pane, app.get().narrow);
  const show = { mail, mailsOpen: true, mailId: mail.id, mailsHold: true };
  if (choice.kind === 'open') return openTab('chat', { pane: choice.pane, title: mailTitle(mail), state: { ...(choice.chatId ? { chatId: choice.chatId } : {}), ...show } });
  if (choice.kind === 'activate') {
    activateTab(choice.tabId);
    updateTabState(choice.tabId, choice.attach ? { ...MAILS_RESET, ...show, focusKey: nextFocus(choice.tabId) } : { ...show, focusKey: nextFocus(choice.tabId) });
    if (choice.attach) renameTab(choice.tabId, mailTitle(mail));
    return choice.tabId;
  }
  updateTabState(choice.tabId, { chatId: choice.chatId, composing: false, ...MAILS_RESET, ...show, focusKey: nextFocus(choice.tabId) });
  renameTab(choice.tabId, mailTitle(mail));
  activateTab(choice.tabId);
  return choice.tabId;
}
